import { useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui'
import { ArtifactImage } from '@/components/arena/ArtifactImage'
import { ArtifactVideo } from '@/components/arena/ArtifactVideo'
import { useArenaAgents, useArenaRun, useArenaVote, useCreateArenaRun } from '@/api/arenaHooks'
import type { ArenaResult, JsonValue } from '@/api/arena'

function resultStatusClass(status: string): string {
  switch (status) {
    case 'success':
      return 'bg-emerald-500/15 text-emerald-400'
    case 'failed':
      return 'bg-red-500/15 text-red-400'
    case 'running':
      return 'bg-blue-500/15 text-blue-400'
    default:
      return 'bg-foreground/10 text-foreground/70'
  }
}

function num(v: JsonValue | undefined): number | null {
  return typeof v === 'number' ? v : null
}

function MetricsStrip({ result }: { result: ArenaResult }) {
  const { t } = useTranslation('pages')
  const usage = result.usage || {}
  const lat = result.latency_ms != null ? `${(result.latency_ms / 1000).toFixed(1)}s` : '–'
  // Show the metric that fits the agent: image/video agents bill per-asset (0 text
  // tokens), so show counts; text agents show tokens. Never render a bare "tok 0/0".
  const tin = num(usage['input_tokens']) ?? 0
  const tout = num(usage['output_tokens']) ?? 0
  const imgs = num(usage['images_generated']) ?? 0
  const vids = num(usage['videos_generated']) ?? 0
  const vsec = num(usage['video_seconds']) ?? 0
  const cost = num(usage['token_cost_usd']) ?? 0
  return (
    <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs font-medium text-foreground/50">
      <span>{t('arena.latency', { time: lat })}</span>
      {imgs > 0 && <span>{t('arena.nImages', { count: imgs })}</span>}
      {vids > 0 && (
        <span>
          {t('arena.nClips', { count: vids })}
          {vsec > 0 ? t('arena.clipSeconds', { seconds: vsec }) : ''}
        </span>
      )}
      {imgs === 0 && vids === 0 && (tin > 0 || tout > 0) && (
        <span>{t('arena.tokenUsage', { input: tin, output: tout })}</span>
      )}
      {cost > 0 && (
        <span title={t('arena.estimatedCostTitle')}>
          {t('arena.estimatedCost', { cost: cost < 1 ? cost.toFixed(4) : cost.toFixed(2) })}
        </span>
      )}
    </div>
  )
}

export default function ArenaRunPage() {
  const { t } = useTranslation('pages')
  const { runId } = useParams<{ runId: string }>()
  const navigate = useNavigate()
  const runQuery = useArenaRun(runId)
  const agentsQuery = useArenaAgents()
  const vote = useArenaVote(runId ?? '')
  const createRun = useCreateArenaRun()

  const [revealed, setRevealed] = useState(false)

  const detail = runQuery.data
  const run = detail?.run
  const results = useMemo(
    () => [...(detail?.results ?? [])].sort((a, b) => a.sub_index - b.sub_index),
    [detail],
  )
  const outputKind =
    agentsQuery.data?.agents.find((a) => a.agent_type === run?.agent_type)?.output_kind ?? 'script'
  const blindActive = !!run?.blind && !revealed

  async function castWinner(winner: ArenaResult) {
    if (!runId) return
    const opponents = results.filter((r) => r.id !== winner.id && r.status === 'success')
    if (winner.status !== 'success' || opponents.length === 0) return
    await Promise.all(
      opponents.map((loser) =>
        vote.mutateAsync({
          winner_result_id: winner.id,
          loser_result_id: loser.id,
          blind: !!run?.blind,
          idempotency_key: `${runId}:${winner.id}>${loser.id}`,
        }),
      ),
    )
    setRevealed(true)
  }

  async function rerun() {
    if (!run) return
    const res = await createRun.mutateAsync({
      agent_type: run.agent_type,
      models: run.models,
      input: run.input,
      blind: run.blind,
    })
    navigate(`/arena/${res.run_id}`)
  }

  if (runQuery.isLoading) {
    return <div className="mx-auto max-w-5xl px-4 py-8 text-foreground/60">{t('arena.loadingRun')}</div>
  }
  if (!run) {
    return <div className="mx-auto max-w-5xl px-4 py-8 text-red-400">{t('arena.runNotFound')}</div>
  }

  const votingDone = results.some((r) => r.model === run.winner_model) || revealed
  // Voting is pairwise — only offer it when there are ≥2 successful results to
  // compare. A single-model run just shows its output (the user asked for this).
  const canVote = results.filter((r) => r.status === 'success').length >= 2

  return (
    <div className="mx-auto max-w-6xl px-4 py-8">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Link to="/arena" className="text-sm text-foreground/60 hover:text-foreground">
            {t('arena.backToArena')}
          </Link>
          <span className="text-lg font-semibold text-foreground">{run.agent_type}</span>
          <span className="rounded-full bg-foreground/10 px-2 py-0.5 text-xs font-medium text-foreground/70">
            {run.status}
          </span>
          {run.blind && (
            <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">{t('arena.blind')}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Link
            to={`/arena/leaderboard/${run.agent_type}`}
            className="text-sm text-foreground/60 hover:text-foreground"
          >
            {t('arena.leaderboard')}
          </Link>
          <Button onClick={rerun} disabled={createRun.isPending}>
            {createRun.isPending ? t('arena.reRunning') : t('arena.reRun')}
          </Button>
        </div>
      </div>

      {run.winner_model && (
        <div className="mb-4 rounded-md border border-primary/30 bg-primary/[0.04] px-4 py-2 text-sm text-foreground">
          {t('arena.winner', { model: run.winner_model })}
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {results.map((r) => {
          const label = blindActive ? t('arena.modelBlind', { letter: String.fromCharCode(65 + r.sub_index) }) : r.model
          const isWinner = run.winner_model === r.model && !blindActive
          return (
            <div
              key={r.id}
              className={
                'flex flex-col rounded-lg border p-4 ' +
                (isWinner ? 'border-primary/40 bg-primary/[0.03]' : 'border-border-subtle')
              }
            >
              <div className="mb-2 flex items-center justify-between">
                <span className="truncate text-sm font-medium text-foreground" title={label}>
                  {label}
                </span>
                <span className={'rounded-full px-2 py-0.5 text-xs font-medium ' + resultStatusClass(r.status)}>
                  {r.status}
                </span>
              </div>

              <div className="min-h-[120px] flex-1">
                {r.status === 'pending' || r.status === 'running' ? (
                  <div className="text-xs font-medium text-foreground/50">{t('arena.generating')}</div>
                ) : r.status === 'failed' ? (
                  <div className="text-xs font-medium text-red-400">{r.error ?? t('arena.failed')}</div>
                ) : outputKind === 'image' ? (
                  <ArtifactImage resultId={r.id} alt={label} />
                ) : outputKind === 'video' ? (
                  <ArtifactVideo resultId={r.id} />
                ) : (
                  <ScriptOutput result={r} />
                )}
              </div>

              <MetricsStrip result={r} />

              {canVote &&
                (votingDone ? (
                  isWinner ? (
                    <div className="mt-3 text-center text-xs font-medium text-primary">{t('arena.winnerBadge')}</div>
                  ) : null
                ) : (
                  <Button
                    className="mt-3 w-full"
                    onClick={() => castWinner(r)}
                    disabled={r.status !== 'success' || vote.isPending}
                  >
                    {t('arena.pickWinner')}
                  </Button>
                ))}
            </div>
          )
        })}
      </div>
    </div>
  )
}

function ScriptOutput({ result }: { result: ArenaResult }) {
  const { t } = useTranslation('pages')
  const out = result.outputs || {}
  const rawScript = out['out_script']
  const script = typeof rawScript === 'string' ? rawScript : ''
  const scenes = num(out['out_scene_count'])
  return (
    <div>
      {scenes != null && <div className="mb-1 text-xs font-medium text-foreground/50">{t('arena.nScenes', { count: scenes })}</div>}
      <pre className="max-h-72 overflow-auto whitespace-pre-wrap break-words rounded-md bg-foreground/[0.03] p-2 text-xs font-medium text-foreground/80">
        {script || t('arena.emptyScript')}
      </pre>
    </div>
  )
}
